import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X, Youtube } from 'lucide-react';
import { toast } from 'sonner';

const VideoManager = ({ videos = [], onVideosUpdate }) => {
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const MAX_VIDEOS = 5;

  const extractYouTubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))(\?v=)?([^#&?]*).*$/;
    const match = url.match(regExp);
    return (match && match[8].length === 11) ? match[8] : null;
  };

  const handleAddVideo = () => {
    if (!newVideoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    if (videos.length >= MAX_VIDEOS) {
      toast.error(`Maximum ${MAX_VIDEOS} videos allowed`);
      return;
    }

    const videoId = extractYouTubeId(newVideoUrl);
    if (!videoId) {
      toast.error('Invalid YouTube URL');
      return;
    }

    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    const newVideos = [...videos, embedUrl];
    onVideosUpdate(newVideos);
    setNewVideoUrl('');
    toast.success('Video added');
  };

  const handleRemoveVideo = (index) => {
    const newVideos = videos.filter((_, i) => i !== index);
    onVideosUpdate(newVideos);
    toast.success('Video removed');
  };

  return (
    <div className="space-y-4" data-testid="video-manager">
      <label className="text-sm font-medium text-white block">YouTube Videos ({videos.length}/{MAX_VIDEOS})</label>

      {videos.length < MAX_VIDEOS && (
        <div className="flex gap-2">
          <Input value={newVideoUrl} onChange={(e) => setNewVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="bg-white/5 border-white/10 text-white flex-1" data-testid="video-url-input" onKeyPress={(e) => e.key === 'Enter' && handleAddVideo()} />
          <Button onClick={handleAddVideo} className="bg-primary" data-testid="add-video-button">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}

      {videos.length > 0 && (
        <div className="space-y-3">
          {videos.map((video, index) => (
            <div key={index} className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid={`video-item-${index}`}>
              <Youtube className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">Video {index + 1}</p>
                <p className="text-xs text-muted-foreground truncate">{video}</p>
              </div>
              <button onClick={() => handleRemoveVideo(index)} className="w-8 h-8 rounded-full bg-destructive/10 hover:bg-destructive/20 flex items-center justify-center transition-colors" data-testid={`remove-video-${index}`}>
                <X className="w-4 h-4 text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}

      {videos.length === 0 && (
        <div className="text-center py-8 border border-dashed border-white/10 rounded-xl" data-testid="empty-videos">
          <p className="text-muted-foreground">No videos added</p>
        </div>
      )}
    </div>
  );
};

export default VideoManager;